import type { INodeDevice } from "@shared/common/node/type";
import type { CampaignScheduleType } from "@shared/common/types/campaign";
import type { ISO8601DateTime } from "@shared/common/types/common";
import type { DayOfWeek, IRecurrenceRule } from "@shared/common/types/schedule";

export const getScheduleInfo = (schedule: CampaignScheduleType): string => {
  if (schedule.type === "DATETIME_RANGE") {
    const rruleInfo = schedule.rrule ? ` [RRULE: ${schedule.rrule.freq}]` : "";
    return `${schedule.start}~${schedule.end}${rruleInfo}`;
  }

  return "";
};

export type CheckResult =
  | { type: "SUCCESS"; slots: number[] }
  | { type: "FAIL"; message: string }
  | { type: "IGNORE" };

export const filterEmptySchedule = (
  schedules: CampaignScheduleType[]
): CampaignScheduleType[] => {
  return schedules.filter((schedule) => {
    if (schedule.type === "DATETIME_RANGE") {
      return schedule.start || schedule.end;
    }
    return true;
  });
};

export type CheckScheduleArgs = {
  currentDate: Date;
  schedule: CampaignScheduleType;
  currentDevice: INodeDevice;
};

export const checkSchedule = ({
  currentDate,
  schedule,
  currentDevice,
}: CheckScheduleArgs): CheckResult => {
  if (!schedule || !schedule.type) {
    return { type: "IGNORE" };
  }

  if (schedule.type === "AND") {
    const results = schedule.value
      .map((schedule) =>
        checkSchedule({ schedule, currentDate, currentDevice })
      )
      .filter((result) => result.type !== "IGNORE");

    if (results.length === 0) {
      return { type: "IGNORE" };
    }

    const successResults: { type: "SUCCESS"; slots: number[] }[] =
      results.filter(
        (result): result is { type: "SUCCESS"; slots: number[] } =>
          result.type === "SUCCESS"
      );

    if (successResults.length === results.length) {
      return {
        type: "SUCCESS",
        slots: successResults.reduce(
          (acc, cur) => [...acc, ...cur.slots],
          [] as number[]
        ),
      };
    }

    return {
      type: "FAIL",
      message: `AND condition failed: ${JSON.stringify(schedule)}`,
    };
  }

  if (schedule.type === "OR") {
    const results = schedule.value
      .map((schedule) =>
        checkSchedule({ schedule, currentDate, currentDevice })
      )
      .filter((result) => result.type !== "IGNORE");

    if (results.length === 0) {
      return { type: "IGNORE" };
    }

    const successResults: { type: "SUCCESS"; slots: number[] }[] =
      results.filter(
        (result): result is { type: "SUCCESS"; slots: number[] } =>
          result.type === "SUCCESS"
      );

    if (successResults.length > 0) {
      return {
        type: "SUCCESS",
        slots: successResults.reduce(
          (acc, cur) => [...acc, ...cur.slots],
          [] as number[]
        ),
      };
    }

    return {
      type: "FAIL",
      message: `OR condition failed: ${JSON.stringify(schedule)}`,
    };
  }

  if (schedule.type === "DATETIME_RANGE") {
    const date = currentDate;
    const time = date.getTime();

    // 스케줄 정보가 없을 시 true 로 간주
    const deviceCheckResult =
      !schedule.device ||
      schedule.device === currentDevice ||
      schedule.device === "ALL";

    if (!deviceCheckResult) {
      return {
        type: "FAIL",
        message: `Device mismatch: expect ${schedule.device}, actual ${currentDevice}`,
      };
    }

    // RRULE이 있는 경우
    if (schedule.rrule && schedule.start && schedule.end) {
      // RFC 5545: DTSTART~DTEND의 duration이 매번 반복됨
      const matchesRRule = checkRecurrenceRule(
        schedule.rrule,
        date,
        schedule.start,
        schedule.end
      );

      if (matchesRRule) {
        return {
          type: "SUCCESS",
          slots: typeof schedule.slot === "number" ? [schedule.slot] : [],
        };
      }

      return {
        type: "FAIL",
        message: `expect:${getScheduleInfo(schedule)}(${
          schedule.device
        }), actual:${date.toISOString()}(${currentDevice}) [RRULE not matched]`,
      };
    }

    const baseTimeCheck =
      (!schedule.start ||
        // 스케줄 데이터 형식이 ISO 8601이 아닌 경우가 있음 (YYYY-MM-DD HH:mm:ss)
        // 15.2버전 사파리에서 YYYY-MM-DD HH:mm:ss 형식을 invalid date로 처리하여 포맷 변경
        time >= new Date(schedule.start.replace(/ /g, "T")).getTime()) &&
      (!schedule.end ||
        time <= new Date(schedule.end.replace(/ /g, "T")).getTime());
    if (baseTimeCheck) {
      return {
        type: "SUCCESS",
        slots: typeof schedule.slot === "number" ? [schedule.slot] : [],
      };
    }

    return {
      type: "FAIL",
      message: `expect:${getScheduleInfo(schedule)}(${
        schedule.device
      }), actual:${date.toISOString()}(${currentDevice})`,
    };
  }

  return { type: "IGNORE" };
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// RFC 5545 표준 구현을 위한 헬퍼 함수들

/**
 * RFC 5545 표준 준수: RecurrenceRule과 Duration을 기반으로 특정 날짜가 반복 범위 내에 있는지 확인
 * @param rule 반복 규칙
 * @param targetDate 확인할 날짜/시각
 * @param startDate 스케줄 시작 날짜/시각 (ISO8601DateTime)
 * @param endDate 스케줄 종료 날짜/시각 (ISO8601DateTime)
 * @returns targetDate가 반복 이벤트의 duration 범위 내에 있으면 true
 *
 * RFC 5545 동작:
 * - DTSTART~DTEND의 duration이 매번 반복됨
 * - 예: 2025/10/13(일) 22:00 ~ 2025/10/16(수) 03:00 + byDay=[MO]
 *   → 매주 월요일 22:00부터 2일 5시간 동안 활성화
 *   → 10/14 22:00 ~ 10/17 03:00, 10/21 22:00 ~ 10/24 03:00, ...
 *
 * 타임존 정책:
 * - 로컬 타임존 기준으로 요일 계산 (getDay)
 * - 저장 시점의 로컬 요일과 조회 시점의 로컬 요일이 일치
 * - 한국에서 "목요일 09:00" 설정 → 한국에서 조회 시 목요일에 활성화
 * - 각 지역의 로컬 시간 기준으로 요일 판단
 *
 * 전제조건:
 * - startDate와 endDate는 이 함수 호출 전에 반드시 존재해야 합니다
 * - 호출하는 쪽에서 null/undefined 체크를 완료한 상태로 호출해야 합니다
 */
export const checkRecurrenceRule = (
  rule: IRecurrenceRule,
  targetDate: Date,
  startDate: ISO8601DateTime,
  endDate: ISO8601DateTime
): boolean => {
  // 1. until 체크
  if (rule.until) {
    const untilDate = new Date(rule.until);
    if (targetDate > untilDate) {
      return false;
    }
  }

  // 2. DTSTART와 DTEND 파싱
  const dtstart = new Date(startDate);
  const dtend = new Date(endDate);
  const duration = dtend.getTime() - dtstart.getTime();

  if (duration < 0) {
    return false; // 종료 시각이 시작 시각보다 이전이면 미노출
  }

  // 3. FREQ별 처리
  switch (rule.freq) {
    case "WEEKLY":
      return checkWeeklyRecurrence(rule, targetDate, dtstart, duration);

    case "DAILY":
      return checkDailyRecurrence(rule, targetDate, dtstart, duration);

    case "MONTHLY":
      return checkMonthlyRecurrence(rule, targetDate, dtstart, duration);

    case "YEARLY":
      return checkYearlyRecurrence(rule, targetDate, dtstart, duration);

    default:
      return false;
  }
};

/**
 * WEEKLY 반복 체크 (RFC 5545 준수)
 */
const checkWeeklyRecurrence = (
  rule: IRecurrenceRule,
  targetDate: Date,
  dtstart: Date,
  duration: number
): boolean => {
  if (!rule.byDay || rule.byDay.length === 0) {
    return false; // byDay가 없으면 매칭 불가
  }

  const interval = rule.interval || 1;

  // byDay에 포함된 각 요일에 대해 체크
  for (const dayOfWeek of rule.byDay) {
    if (typeof dayOfWeek === "string") {
      // DTSTART부터 targetDate 사이의 모든 해당 요일 occurrence를 찾음
      // 가장 가까운 이전 occurrence를 찾아서 duration 범위 내에 있는지 확인
      const occurrenceStart = findNearestWeeklyOccurrence(
        dtstart,
        dayOfWeek,
        targetDate,
        interval
      );

      if (occurrenceStart) {
        const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);

        // targetDate가 해당 occurrence의 duration 범위 내에 있는지 확인
        if (targetDate >= occurrenceStart && targetDate < occurrenceEnd) {
          return true;
        }
      }
    }
  }

  return false;
};

const dayOfWeekMap: Record<DayOfWeek, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

/**
 * targetDate에 가장 가까운 이전 occurrence 찾기
 * @param dtstart DTSTART 날짜 (시간 포함)
 * @param dayOfWeek 목표 요일 (MO, TU, ...)
 * @param targetDate 확인할 날짜 (시간 포함)
 * @param interval 반복 간격 (1 = 매주, 2 = 격주, ...)
 * @returns 가장 가까운 이전 occurrence 시작 시각, 없으면 null
 */
const findNearestWeeklyOccurrence = (
  dtstart: Date,
  dayOfWeek: DayOfWeek,
  targetDate: Date,
  interval: number
): Date | null => {
  const targetDayOfWeek = dayOfWeekMap[dayOfWeek];
  const dtstartDayOfWeek = dtstart.getDay(); // 로컬 타임존 기준 요일

  // DTSTART부터 첫 번째 해당 요일 occurrence 찾기 (시간 유지)
  const daysToFirstOccurrence = (targetDayOfWeek - dtstartDayOfWeek + 7) % 7;

  // 밀리초 단위로 계산하여 시간을 정확히 유지
  const firstOccurrenceTime =
    dtstart.getTime() + daysToFirstOccurrence * MS_PER_DAY;
  const firstOccurrence = new Date(firstOccurrenceTime);

  // targetDate가 첫 occurrence 이전이면 null
  if (targetDate.getTime() < firstOccurrence.getTime()) {
    return null;
  }

  // firstOccurrence부터 targetDate까지 몇 주가 지났는지 계산
  const msDiff = targetDate.getTime() - firstOccurrence.getTime();
  const weeksDiff = Math.floor(msDiff / (7 * MS_PER_DAY));

  // interval을 고려한 가장 가까운 occurrence 인덱스
  const occurrenceIndex = Math.floor(weeksDiff / interval) * interval;

  // 가장 가까운 occurrence 계산 (밀리초 단위)
  const nearestOccurrenceTime =
    firstOccurrence.getTime() + occurrenceIndex * 7 * MS_PER_DAY;
  const nearestOccurrence = new Date(nearestOccurrenceTime);

  return nearestOccurrence;
};

/**
 * DAILY 반복 체크 (RFC 5545 준수)
 */
const checkDailyRecurrence = (
  rule: IRecurrenceRule,
  targetDate: Date,
  dtstart: Date,
  duration: number
): boolean => {
  const interval = rule.interval || 1;
  const daysSinceDtstart = Math.floor(
    (targetDate.getTime() - dtstart.getTime()) / MS_PER_DAY
  );

  if (daysSinceDtstart < 0) {
    return false; // DTSTART 이전
  }

  // interval 체크
  const dayOffset = daysSinceDtstart % interval;
  if (dayOffset !== 0) {
    return false; // 반복 간격에 해당하지 않음
  }

  // 해당 occurrence 계산
  const occurrenceStart = new Date(
    dtstart.getTime() + daysSinceDtstart * MS_PER_DAY
  );
  const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);

  return targetDate >= occurrenceStart && targetDate < occurrenceEnd;
};

/**
 * MONTHLY 반복 체크 (RFC 5545 준수)
 * 로컬 타임존 기준으로 월 계산
 */
const checkMonthlyRecurrence = (
  rule: IRecurrenceRule,
  targetDate: Date,
  dtstart: Date,
  duration: number
): boolean => {
  // 간단한 구현: byMonthday나 byDay 지원
  // 실제 구현은 더 복잡할 수 있음
  const monthsDiff =
    (targetDate.getFullYear() - dtstart.getFullYear()) * 12 +
    (targetDate.getMonth() - dtstart.getMonth());

  const interval = rule.interval || 1;
  if (monthsDiff < 0 || monthsDiff % interval !== 0) {
    return false;
  }

  const occurrenceStart = new Date(dtstart);
  occurrenceStart.setMonth(occurrenceStart.getMonth() + monthsDiff);
  const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);

  return targetDate >= occurrenceStart && targetDate < occurrenceEnd;
};

/**
 * YEARLY 반복 체크 (RFC 5545 준수)
 * 로컬 타임존 기준으로 연도 계산
 */
const checkYearlyRecurrence = (
  rule: IRecurrenceRule,
  targetDate: Date,
  dtstart: Date,
  duration: number
): boolean => {
  const yearsDiff = targetDate.getFullYear() - dtstart.getFullYear();
  const interval = rule.interval || 1;

  if (yearsDiff < 0 || yearsDiff % interval !== 0) {
    return false;
  }

  const occurrenceStart = new Date(dtstart);
  occurrenceStart.setFullYear(occurrenceStart.getFullYear() + yearsDiff);
  const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);

  return targetDate >= occurrenceStart && targetDate < occurrenceEnd;
};
